import { safariPrepFor } from '@/utils/pdfTextLayerSafari';

export interface UserSelectAncestor {
  tag: string;
  className: string;
  id: string;
  userSelect: string;
  webkitUserSelect: string;
  pointerEvents: string;
  draggable: boolean;
  opacity: string;
  visibility: string;
  contentVisibility: string;
}

export const PDF_DEBUG_STORAGE_KEY = 'clinio.pdfdebug';

function debugSearch(search?: string): string {
  if (search !== undefined) return search;
  try {
    return window.location.search;
  } catch {
    return '';
  }
}

/**
 * `?pdfdebug=1` is stored before the patient page drops unknown query params.
 * `?pdfdebug=0` clears it. The console can also set the same key.
 */
export function rememberPdfDebugFromLocation(search?: string): void {
  if (typeof window === 'undefined') return;
  let flag: string | null = null;
  try {
    flag = new URLSearchParams(debugSearch(search)).get('pdfdebug');
  } catch {
    return;
  }
  try {
    if (flag === '1') window.localStorage.setItem(PDF_DEBUG_STORAGE_KEY, '1');
    else if (flag === '0') window.localStorage.removeItem(PDF_DEBUG_STORAGE_KEY);
  } catch {
    // Private mode can reject localStorage. The query flag still works for this load.
  }
}

export function pdfDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  rememberPdfDebugFromLocation();
  try {
    const flag = new URLSearchParams(window.location.search).get('pdfdebug');
    if (flag === '1') return true;
    if (flag === '0') return false;
  } catch {
    return false;
  }
  try {
    return window.localStorage.getItem(PDF_DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function userSelectAncestors(node: Element | null): UserSelectAncestor[] {
  const rows: UserSelectAncestor[] = [];
  let current: Element | null = node;
  while (current) {
    const style = getComputedStyle(current);
    const element = current instanceof HTMLElement ? current : null;
    rows.push({
      tag: current.tagName,
      className: typeof current.className === 'string' ? current.className.slice(0, 120) : '',
      id: current.id,
      userSelect: style.getPropertyValue('user-select') || style.userSelect,
      webkitUserSelect: style.getPropertyValue('-webkit-user-select'),
      pointerEvents: style.pointerEvents,
      draggable: Boolean(element?.draggable),
      opacity: style.opacity,
      visibility: style.visibility,
      contentVisibility: style.getPropertyValue('content-visibility'),
    });
    current = current.parentElement;
  }
  return rows;
}

export interface TextSpanSample {
  text: string;
  fontSize: string;
  transform: string;
  width: number;
  height: number;
}

/** Text spans pdf.js laid out. `.markedContent` wrappers are height 0 on purpose. */
export function isSelectableTextSpan(span: Element): boolean {
  if (!(span instanceof HTMLElement)) return false;
  if (span.classList.contains('markedContent')) return false;
  return (span.textContent ?? '').trim().length > 0;
}

export function textLayerSnapshot(textLayer: HTMLElement): {
  spanCount: number;
  zeroSize: number;
  scaleFactor: string;
  samples: TextSpanSample[];
} {
  const spans = [...textLayer.querySelectorAll('span')];
  let zeroSize = 0;
  const samples: TextSpanSample[] = [];
  for (const span of spans) {
    if (!isSelectableTextSpan(span)) continue;
    const box = span.getBoundingClientRect();
    if (box.width < 0.5 || box.height < 0.5) zeroSize += 1;
    if (samples.length < 5) {
      const style = getComputedStyle(span);
      samples.push({
        text: (span.textContent ?? '').slice(0, 40),
        fontSize: style.fontSize,
        transform: style.transform,
        width: Math.round(box.width * 10) / 10,
        height: Math.round(box.height * 10) / 10,
      });
    }
  }
  return {
    spanCount: spans.length,
    zeroSize,
    scaleFactor: textLayer.style.getPropertyValue('--scale-factor'),
    samples,
  };
}

let listenerInstalled = false;

/**
 * Temporary. Enable with `?pdfdebug=1` (stored as clinio.pdfdebug) or
 * `localStorage.setItem('clinio.pdfdebug','1')`, then open the PDF.
 * Console lines are prefixed `[pdfdebug]`.
 */
export function installPdfSelectionDebug(
  textLayer: HTMLElement,
  extra?: Record<string, unknown>,
): void {
  if (!pdfDebugEnabled()) return;
  const span = textLayer.querySelector('span');
  console.info(
    '[pdfdebug] Kopiér alle [pdfdebug]-linjer. Forventet: macOS 26.6.2 (25G83), Safari 26.6.2. Chrome på samme Mac virker.',
  );
  console.info('[pdfdebug] environment', {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    offscreenCanvas: typeof OffscreenCanvas !== 'undefined',
    ...extra,
  });
  const layer = textLayerSnapshot(textLayer);
  const prep = safariPrepFor(textLayer);
  const spanRows = [...textLayer.querySelectorAll('span')].map((node, index) => {
    const box = node.getBoundingClientRect();
    const style = getComputedStyle(node);
    const record = prep?.spans[index];
    return {
      text: (node.textContent ?? '').slice(0, 40),
      beforeFont: record?.inlineFont ?? '',
      beforeTransform: record?.inlineTransform ?? '',
      writtenFont: record?.appliedFont ?? '',
      writtenWidth: record?.appliedWidth ?? '',
      writtenHeight: record?.appliedHeight ?? '',
      computedFont: style.fontSize,
      computedTransform: style.transform,
      width: Math.round(box.width * 10) / 10,
      height: Math.round(box.height * 10) / 10,
      keptOriginal: record?.keptOriginal ?? null,
    };
  });
  const keptOriginal = prep?.spans.filter((row) => row.keptOriginal).length ?? 0;
  let untransformed = 0;
  for (const node of textLayer.querySelectorAll('span')) {
    if (!isSelectableTextSpan(node)) continue;
    const box = node.getBoundingClientRect();
    const transform = getComputedStyle(node).transform;
    if (box.width >= 0.5 && box.height >= 0.5 && (transform === 'none' || transform === '')) {
      untransformed += 1;
    }
  }
  console.info(
    `[pdfdebug] summary zeroSize=${layer.zeroSize} spanCount=${layer.spanCount} scaleFactor=${layer.scaleFactor || '?'} transformsCleared=${prep?.transformsCleared ?? 0} keptOriginal=${keptOriginal} restored=${prep?.restored ?? 0} untransformed=${untransformed}`,
  );
  console.info('[pdfdebug] spans', spanRows);
  console.info('[pdfdebug] text layer', layer);
  console.info('[pdfdebug] ancestors from first span', userSelectAncestors(span ?? textLayer));

  if (listenerInstalled) return;
  listenerInstalled = true;

  const onMouseDown = (event: MouseEvent) => {
    const point = document.elementFromPoint(event.clientX, event.clientY);
    console.info('[pdfdebug] mousedown', {
      defaultPrevented: event.defaultPrevented,
      target: describe(event.target),
      elementFromPoint: describe(point),
      ancestors: userSelectAncestors(point),
    });
    window.setTimeout(() => {
      const selection = window.getSelection();
      console.info('[pdfdebug] after mousedown', {
        defaultPrevented: event.defaultPrevented,
        selected: selection?.toString().slice(0, 80) ?? '',
        collapsed: selection?.isCollapsed ?? null,
      });
    }, 0);
  };

  document.addEventListener('mousedown', onMouseDown, true);
  document.addEventListener(
    'dblclick',
    (event) => {
      console.info('[pdfdebug] dblclick', {
        defaultPrevented: event.defaultPrevented,
        target: describe(event.target),
        selected: window.getSelection()?.toString().slice(0, 80) ?? '',
      });
    },
    true,
  );
}

function describe(target: EventTarget | Element | null): string {
  if (!(target instanceof Element)) return String(target);
  const className = typeof target.className === 'string' ? target.className.slice(0, 80) : '';
  return `${target.tagName}${target.id ? `#${target.id}` : ''}${className ? `.${className.split(/\s+/).join('.')}` : ''}`;
}
